import { clamp, isUndefined, omitBy } from 'lodash';
import { findElement, isContainer } from '../../format/elements';
import type { Element, FormDefinition } from '../../format/form-definition';
import { type InsertionPosition, landingOf, leavesInPlace } from '../elementInsertion';

const mapElements = (form: FormDefinition, change: (element: Element) => Element | undefined): FormDefinition => {
  const rebuild = (elements: Element[]): Element[] => {
    let changed = false;
    const next = elements.flatMap(element => {
      let self = change(element);
      if (self && isContainer(self)) {
        const inside = rebuild(self.elements);
        if (inside !== self.elements) self = { ...self, elements: inside } as Element;
      }
      changed ||= self !== element;
      return self ? [self] : [];
    });
    return changed ? next : elements;
  };
  const elements = rebuild(form.elements);
  return elements === form.elements ? form : { ...form, elements };
};

export const updateElement = (
  form: FormDefinition,
  id: string,
  change: (element: Element) => Element,
): FormDefinition => {
  return mapElements(form, element => (element.id === id ? change(element) : element));
};

export const insertElement = (form: FormDefinition, element: Element, at: InsertionPosition): FormDefinition => {
  const insert = (elements: Element[]) => {
    const index = clamp(at.index, 0, elements.length);
    return elements.toSpliced(index, 0, element);
  };
  if (at.parentId === null) return { ...form, elements: insert(form.elements) };
  return updateElement(form, at.parentId, container =>
    isContainer(container) ? ({ ...container, elements: insert(container.elements) } as Element) : container,
  );
};

export const removeElement = (form: FormDefinition, id: string): FormDefinition => {
  return mapElements(form, element => (element.id === id ? undefined : element));
};

export const moveElement = (form: FormDefinition, id: string, to: InsertionPosition): FormDefinition => {
  const element = findElement(form, id);
  const now = landingOf(form, id);
  if (!element || !now || leavesInPlace(form, id, to)) return form;
  const rest = removeElement(form, id);
  if (to.parentId !== null && !findElement(rest, to.parentId)) return form;
  const index = to.parentId === now.parentId && to.index > now.index ? to.index - 1 : to.index;
  return insertElement(rest, element, { parentId: to.parentId, index });
};

export const withoutUndefined = <T extends object>(object: T): T => {
  return omitBy(object, isUndefined) as T;
};

export const withKey = <E extends Element>(element: E, key: string, value: unknown): E =>
  withoutUndefined({ ...element, [key]: value });
